import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import MemberPicker from './MemberPicker';
import { getCycleRoster } from '../../utils/shapeUpApi';

const toInputDate = (date) => date.toISOString().slice(0, 10);

/**
 * Shape Up runs on fixed cycles, so a new one defaults to six weeks from today.
 */
const defaultDates = () => {
  const start = new Date();
  const end = new Date();
  end.setDate(end.getDate() + 42);
  return { startDate: toInputDate(start), endDate: toInputDate(end) };
};

/**
 * Create or edit a cycle, and settle who is working it.
 *
 * The roster lives here rather than on a separate screen because a cycle and the
 * people in it are decided in the same conversation. Most cycles carry most of
 * the previous crew, so the common path is one click on "copy from" and then a
 * couple of adjustments.
 */
const CycleDialog = ({
  open,
  onClose,
  onSubmit,
  cycle = null,
  candidates = [],
  recentCycles = [],
}) => {
  const isEdit = Boolean(cycle);
  const [form, setForm] = useState({ name: '', status: 'active', ...defaultDates() });
  const [members, setMembers] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const loadRoster = useCallback(async (cycleId) => {
    setLoadingRoster(true);
    try {
      const data = await getCycleRoster(cycleId);
      setMembers(data.members || []);
    } catch {
      // A roster that will not load should not block editing the dates. It comes
      // back empty, and saving with it empty is an explicit act the user can see.
      setMembers([]);
    } finally {
      setLoadingRoster(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setNotice(null);

    if (cycle) {
      setForm({
        name: cycle.name || '',
        status: cycle.status || 'active',
        startDate: (cycle.startDate || '').slice(0, 10),
        endDate: (cycle.endDate || '').slice(0, 10),
      });
      loadRoster(cycle.id);
    } else {
      setForm({ name: '', status: 'active', ...defaultDates() });
      setMembers([]);
    }
  }, [open, cycle, loadRoster]);

  const onChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  // Copying happens in local state rather than through the copy endpoint, so it
  // works identically whether the cycle exists yet or not, and stays undoable
  // until the dialog is saved.
  const copyFrom = (sourceCycle) => {
    const existing = new Set(members.map(member => member.userId));
    const additions = (sourceCycle.members || []).filter(member => !existing.has(member.userId));
    if (!additions.length) {
      setNotice(`Everyone on ${sourceCycle.name} is already here.`);
      return;
    }
    setMembers([...members, ...additions]);
    setNotice(`Added ${additions.length} from ${sourceCycle.name}.`);
  };

  const submit = async () => {
    if (!form.name.trim()) {
      setError('Give the cycle a name.');
      return;
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      setError('The end date must come after the start date.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        name: form.name.trim(),
        status: form.status,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        members: members.map(member => ({ userId: member.userId, role: member.role })),
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || (isEdit ? 'Could not update the cycle.' : 'Could not create the cycle.'));
    } finally {
      setSaving(false);
    }
  };

  const copyable = recentCycles
    .filter(candidate => candidate.id !== cycle?.id && (candidate.members || []).length > 0)
    .slice(0, 3);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit cycle' : 'New cycle'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              fullWidth
              required
              name="name"
              label="Cycle name"
              placeholder="Cycle 1 — Autumn"
              value={form.name}
              onChange={onChange}
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              name="startDate"
              label="Starts"
              value={form.startDate}
              onChange={onChange}
              disabled={saving}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              name="endDate"
              label="Ends"
              value={form.endDate}
              onChange={onChange}
              disabled={saving}
              InputLabelProps={{ shrink: true }}
              helperText="Six weeks is the usual Shape Up cycle."
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="cycle-status-label">Status</InputLabel>
              <Select
                labelId="cycle-status-label"
                name="status"
                value={form.status}
                onChange={onChange}
                label="Status"
                disabled={saving}
              >
                <MenuItem value="planning">Planning</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="cooldown">Cool-down</MenuItem>
                <MenuItem value="complete">Complete</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2">Who is working this cycle</Typography>
            <Typography variant="caption" color="text.secondary">
              A roster for this cycle only. It records who is doing the work and
              changes nobody&apos;s access.
            </Typography>
          </Grid>

          {copyable.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                Same crew as:
              </Typography>
              {copyable.map(source => (
                <Button
                  key={source.id}
                  size="small"
                  variant="text"
                  onClick={() => copyFrom(source)}
                  disabled={saving || loadingRoster}
                >
                  {source.name}
                </Button>
              ))}
            </Grid>
          )}

          {notice && (
            <Grid item xs={12}>
              <Alert severity="info" onClose={() => setNotice(null)} sx={{ py: 0 }}>
                {notice}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <MemberPicker
              candidates={candidates}
              value={members}
              onChange={setMembers}
              disabled={saving || loadingRoster}
              label="Add someone to this cycle"
              emptyHint={loadingRoster
                ? 'Loading the roster…'
                : 'Nobody on this cycle yet. Bets can still be placed; you just will not see who owns them.'}
              noCandidatesHint="Invite people to the organization first."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving
            ? (isEdit ? 'Saving…' : 'Creating…')
            : (isEdit ? 'Save changes' : 'Create cycle')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CycleDialog;
